import {ChevronRightIcon, MinusIcon, PlusIcon} from '@heroicons/react/solid';
import React, {useState} from 'react';

import {Button, Table, TBody, Td, Th, Thead, Tr} from '@/components';
import type {AfriexOTCTrade} from '@/types';
import {formatValues} from '@/utils/dashboard';
import * as formatDate from '@/utils/date';

import OTCTradeSummary from './OTCTradeSummary';
import type {OTCTradeListProps} from './types';

type ExtraRowArg = AfriexOTCTrade;

const OTCTradeTable = ({
  items,
  extraRow,
  handleApprove,
}: {
  items: AfriexOTCTrade[];
  extraRow?: (user?: ExtraRowArg) => React.ReactNode;
  handleApprove: OTCTradeListProps['handleApprove'];
}) => {
  return (
    <Table>
      <Thead>
        <Tr enableHover={false}>
          <Th className="w-20">&nbsp;</Th>
          <Th>Sent</Th>
          <Th>Received</Th>
          <Th>Rate</Th>
          <Th>Date</Th>
          <Th className="w-20">&nbsp;</Th>
        </Tr>
      </Thead>
      <TBody>
        {items.map((item, index) => (
          <OTCTradeRow
            key={index}
            item={item}
            extraRow={extraRow}
            handleApprove={handleApprove}
            index={index}
          />
        ))}
      </TBody>
    </Table>
  );
};

const OTCTradeRow = ({
  item,
  extraRow,
  handleApprove,
  index,
}: {
  item: AfriexOTCTrade;
  extraRow?: (item?: ExtraRowArg) => React.ReactNode;
  handleApprove: OTCTradeListProps['handleApprove'];
  index: number;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const zebraRow = index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-dark-zebraRow';

  return (
    <>
      <Tr className={zebraRow}>
        <Td isNumeric>
          <Button onClick={() => setIsExpanded(!isExpanded)} variant="unStyled">
            <div
              className={`border-2 border-gray-700 dark:border-dark-borderLight dark:text-[#FCFBF5] text-gray-700 w-5 h-5 flex justify-center rounded hover:bg-cyan-50 ${
                isExpanded && 'bg-cyan-50'
              }`}
            >
              {isExpanded ? (
                <MinusIcon className="w-4 h-4" />
              ) : (
                <PlusIcon className="w-4 h-4" />
              )}
            </div>
          </Button>
        </Td>

        <Td className={`font-medium`}>
          {item.fromAmount?.toLocaleString()} {item.fromSymbol}
        </Td>

        <Td>
          {item.toAmount?.toLocaleString()} {item.toSymbol}
        </Td>

        <Td>{formatValues(Number(item.rate), 'rate', 6)}</Td>

        <Td>{formatDate.DDMonYYYY(item.timestamp?.toString() || '')}</Td>

        <Td>
          <ChevronRightIcon className="w-5 h-5" />
        </Td>
      </Tr>

      {isExpanded && (
        <Tr enableHover={false}>
          <Td colSpan={6} className="p-0">
            <div className="flex py-10 px-8 justify-center bg-sky-50 dark:bg-transparent w-full">
              <OTCTradeSummary
                item={item}
                handleApprove={handleApprove}
                extraRow={extraRow?.(item) || null}
              />
            </div>
          </Td>
        </Tr>
      )}
    </>
  );
};

export default OTCTradeTable;
